import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { useFloating, autoUpdate, offset, flip, shift, useHover, useFocus, useDismiss, useRole, useInteractions, useMergeRefs, FloatingPortal } from '@floating-ui/react';
import { useTooltipStyles } from './Tooltip.Styles';
export function useTooltip({ initialOpen = false, placement = 'top', open: controlledOpen, onOpenChange: setControlledOpen } = {}) {
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);
    const open = controlledOpen ?? uncontrolledOpen;
    const setOpen = setControlledOpen ?? setUncontrolledOpen;
    const data = useFloating({
        placement,
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(5),
            flip({
                crossAxis: placement.includes('-'),
                fallbackAxisSideDirection: 'start',
                padding: 5
            }),
            shift({ padding: 5 })
        ]
    });
    const context = data.context;
    const hover = useHover(context, {
        move: false,
        enabled: controlledOpen == null
    });
    const focus = useFocus(context, {
        enabled: controlledOpen == null
    });
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'tooltip' });
    const interactions = useInteractions([hover, focus, dismiss, role]);
    return React.useMemo(() => ({
        open,
        setOpen,
        ...interactions,
        ...data
    }), [open, setOpen, interactions, data]);
}
const TooltipContext = React.createContext(null);
export const useTooltipContext = () => {
    const context = React.useContext(TooltipContext);
    if (context == null) {
        throw new Error('Tooltip components must be wrapped in <Tooltip />');
    }
    return context;
};
export function Tooltip({ children, ...options }) {
    const tooltip = useTooltip(options);
    return (_jsx(TooltipContext.Provider, { value: tooltip, children: children }));
}
export const TooltipTrigger = React.forwardRef(function TooltipTrigger({ children, asChild = false, ...props }, propRef) {
    const context = useTooltipContext();
    const childrenRef = children.ref;
    const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);
    if (asChild && React.isValidElement(children)) {
        return React.cloneElement(children, context.getReferenceProps({
            ref,
            ...props,
            ...children.props,
            'data-state': context.open ? 'open' : 'closed'
        }));
    }
    return (_jsx("div", { ref: ref, "data-state": context.open ? 'open' : 'closed', ...context.getReferenceProps(props), children: children }));
});
export const TooltipContent = React.forwardRef(function TooltipContent({ style, ...props }, propRef) {
    const styles = useTooltipStyles();
    const context = useTooltipContext();
    const ref = useMergeRefs([context.refs.setFloating, propRef]);
    if (!context.open)
        return null;
    return (_jsx(FloatingPortal, { children: _jsx("div", { ref: ref, className: styles.tooltip, style: {
                ...context.floatingStyles,
                ...style
            }, ...context.getFloatingProps(props) }) }));
});
